import { OrderSideEnum } from '../../lib/enums/order-side.enum';
import { OrderStatusEnum } from '../../lib/enums/order-status.enum';
import { OrderTypeEnum } from '../../lib/enums/order-type.enum';
import lang from '../../lib/language';
import { Order } from '../../lib/models/trading/types';
import { formatCurrency } from '../../lib/util/DataHelpers';
import { convertOrderSideToText, orderStatusToCallStatus } from '../../lib/util/TradingHelpers';
import { getMaxBuyLimitPrice, getMaxSellLimitPrice, validateAggressiveBuyLimitOrder, validateAggressiveSellLimitOrder } from '../../lib/util/TradingValidationsHelper';
import { NullableNumber, NullableString } from '../../lib/util/types';

import { getOrderRejectMessage } from '../../util/order-reject-messages';

export const proccessSuccessMessageTitle = (
  orderType: OrderTypeEnum, orderSide: OrderSideEnum, status?: string, hasLimitFilled?: boolean,
): string => {
  let title = lang.orderMessageTitleFilled(getNameByOrderType(orderType), convertOrderSideToText(orderSide));

  if ((orderType === OrderTypeEnum.Limit || orderType === OrderTypeEnum.Stop) && !hasLimitFilled) {
    title = lang.orderMessageTitlePlaced(getNameByOrderType(orderType), convertOrderSideToText(orderSide));
  }

  if (status === OrderStatusEnum.PartiallyFilled) {
    title = lang.orderMessageTitlePartiallyFilled(getNameByOrderType(orderType), convertOrderSideToText(orderSide));
  }

  return title;
};

export const processFailMessageManager = (
  isRejected: boolean, isExpired: boolean, symbol: string, type: OrderTypeEnum, side: OrderSideEnum, quantity: number,
) => {
  switch (true) {
    case isRejected: return lang.orderMessageBodyRejectedWeb(
      getNameByOrderType(type),
      convertOrderSideToText(side),
      quantity,
      symbol,
    );
    case isExpired: return lang.orderMessageExpired(
      getNameByOrderType(type),
      convertOrderSideToText(side),
      quantity,
      symbol,
    );
    default: return lang.orderMessageTitleError(getNameByOrderType(type), convertOrderSideToText(side));
  }
};

export const processRejectedMessage = (
  orderType: OrderTypeEnum, orderSide: OrderSideEnum, rejectReason?: string,
): { title: string, body: string } => ({
  title: lang.orderMessageTitleRejected(getNameByOrderType(orderType), convertOrderSideToText(orderSide)),
  body: getOrderRejectMessage(rejectReason),
});

export const proccessPartiallyRejectedMessage = (
  symbol: string,
  orderType: OrderTypeEnum,
  orderSide: OrderSideEnum,
  quantity: number,
  filledSoFar?: number,
): { title: string, body: string } => ({
  title: lang.orderMessageTitlePartiallyRejectedTitle(
    getNameByOrderType(orderType),
    convertOrderSideToText(orderSide),
  ),
  body: lang.orderMessageBodyPartiallyRejectedBody(quantity - (filledSoFar ?? 0), quantity, symbol),
});

export const proccessSuccessMessageBody = (
  symbol: string,
  status?: string,
  quantity?: number,
  orderType?: OrderTypeEnum,
  hasLimitFilled?: boolean,
  filledSoFar?: number,
): string => {
  let text = '';
  const limitOrder = orderType === OrderTypeEnum.Limit;
  const marketOrder = orderType === OrderTypeEnum.Market;
  const stopOrder = orderType === OrderTypeEnum.Stop;

  if (quantity) {
    if (marketOrder || (limitOrder && hasLimitFilled)) {
      text = lang.orderMessageBodyFilled(quantity, symbol);
    }

    if ((limitOrder || stopOrder) && !hasLimitFilled) {
      text = lang.orderMessageBodyPlaced(quantity ?? 0, symbol);
    }

    if (status === OrderStatusEnum.PartiallyFilled && filledSoFar) {
      text = lang.orderMessageBodyPartiallyFilled(filledSoFar, quantity, symbol);
    }
  }

  return text;
};

export type OrderResultMessages = {
  callStatus: ReturnType<typeof orderStatusToCallStatus>;
  processFailedMessage: string;
  processPartiallyRejectedMessage?: { title: string, body: string };
  processRejectedMessage?: { title: string, body: string };
  processSuccessMessageTitle: string;
  processSuccessMessageBody: string;
  defaultMessageTitle: string;
};

/**
 * Builds the result-popup messages for a placed order. Shared by every screen that
 * can place one, so the wording stays identical across web and mobile shells.
 */
export const buildOrderResultMessages = (
  symbol: string,
  order: Order,
  requestStatus?: OrderStatusEnum,
  timer?: { showExpired?: boolean, showFillOrder?: boolean },
): OrderResultMessages => {
  const { ordType, orderQty, side, status, filledSoFar, rejectReport } = order;
  const { showExpired, showFillOrder } = timer ?? {};

  const orderName = getNameByOrderType(ordType!);
  const sideText = convertOrderSideToText(side!);
  const quantity = orderQty ?? 0;

  const isRejected = requestStatus === OrderStatusEnum.Rejected;
  const isInProgress = requestStatus === OrderStatusEnum.InProgress;
  const isPartiallyRejected = status === OrderStatusEnum.PartiallyReject;

  /**
   * The popup renders exactly ONE of these and picks it by priority:
   *   success (callStatus READY) > pending > default > rejected > partially rejected > failed.
   * Every slot is filled here and the popup decides which one the user sees.
   */
  return {
    callStatus: orderStatusToCallStatus(status as OrderStatusEnum),

    // the order settled
    processSuccessMessageTitle: isRejected
      ? '' : proccessSuccessMessageTitle(ordType!, side!, status, showFillOrder),
    processSuccessMessageBody: isRejected
      ? '' : proccessSuccessMessageBody(symbol, status, orderQty, ordType, showFillOrder, filledSoFar),

    // the order is still working
    defaultMessageTitle: isInProgress
      ? lang.orderMessageBodyPendingWeb(orderName, sideText, quantity, symbol) : '',

    // the order failed, most specific reason first
    processRejectedMessage: isRejected && rejectReport
      ? processRejectedMessage(ordType!, side!, rejectReport.text) : undefined,
    processPartiallyRejectedMessage: isPartiallyRejected
      ? proccessPartiallyRejectedMessage(symbol, ordType!, side!, orderQty!, filledSoFar) : undefined,
    processFailedMessage: processFailMessageManager(
      isRejected, !!showExpired, symbol, ordType!, side!, quantity,
    ),
  };
};

export const modifyOrderPopUpTitle = (
  ordSide: string,
  showFillOrder: boolean,
  symbol: string,
  type: OrderTypeEnum,
  status?: string,
  orderQty?: number,
): string => {
  let modifyOrderTtile = '';

  const sideLabel = convertOrderSideToText(ordSide);
  if (showFillOrder) {
    const limitLabel = getNameByOrderType(OrderTypeEnum.Limit);
    modifyOrderTtile = status === OrderStatusEnum.PartiallyFilled
      ? lang.orderMessageTitlePartiallyFilled(limitLabel, sideLabel)
      : lang.orderMessageTitleFilled(limitLabel, sideLabel);
  } else {
    modifyOrderTtile = lang.orderMessageModifySuccessWeb(getNameByOrderType(type), sideLabel, orderQty || 0, symbol);
  }

  return modifyOrderTtile;
};

export const modifyOrderPopUpBody = (
  showFillOrder: boolean,
  symbol: string,
  filledSoFar?: number,
  status?: string,
  orderQty?: number,
) => {
  let message = '';

  if (showFillOrder) {
    message = status === OrderStatusEnum.PartiallyFilled && filledSoFar
      ? lang.orderMessageBodyPartiallyFilled(filledSoFar, orderQty || 0, symbol)
      : lang.orderMessageBodyFilled(orderQty || 0, symbol);
  }

  return message;
};

export const validateOrder = (
  price: number,
  target: number,
  type: string,
  isBuyBtnClicked: boolean,
  setControlCheck: (check: boolean) => void,
  setErrorMessage: (message: string) => void,
) => {
  setControlCheck(true);
  setErrorMessage('');

  if (isNaN(price)) {
    setControlCheck(false);
  }
  if ([OrderTypeEnum.Limit, OrderTypeEnum.Stop].includes(type as OrderTypeEnum)) {
    if (type === OrderTypeEnum.Limit) {
      const limitCheck = isBuyBtnClicked
        ? validateAggressiveBuyLimitOrder(price, target)
        : validateAggressiveSellLimitOrder(price, target);

      if (!limitCheck) {
        const lastPrice = isBuyBtnClicked ? getMaxBuyLimitPrice(price) : getMaxSellLimitPrice(price);

        setErrorMessage(
          isBuyBtnClicked
            ? lang.commonTradingAggressiveLimitPriceError1(formatCurrency(lastPrice))
            : lang.commonTradingValidationNewOrderSellAggressiveLimitError1(formatCurrency(lastPrice)),
        );
        setControlCheck(false);
      }
    }

    if (target === 0) {
      const priceType = type === OrderTypeEnum.Limit ? lang.webTradingLimit() : lang.webTradingStop();
      setErrorMessage(lang.commonTradingPriceError(priceType));
      setControlCheck(false);
    }
  }
};

export const getValidationPrice = (
  isMarketClose: boolean,
  askPrice: NullableNumber,
  bidPrice: NullableNumber,
  tradePrice: number | null,
  isBuyOrder?: boolean,
) => {
  if (isMarketClose) {
    return tradePrice;
  }
  return isBuyOrder ? askPrice : bidPrice;
};

export const getNameByOrderType = (orderType: NullableString|undefined): string => {
  switch (orderType) {
    case OrderTypeEnum.Limit:
      return lang.mobileOrderCreateTabLimit();
    case OrderTypeEnum.Market:
      return lang.mobileOrderCreateTabMarket();
    case OrderTypeEnum.Pegged:
      return lang.mobileOrderCreateTabPegged();
    case OrderTypeEnum.Stop:
      return lang.mobileOrderCreateTabStop();
    case OrderTypeEnum.StopLimit:
      return lang.mobileOrderCreateTabStopLimit();
    default:
      return '';
  }
};
